import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, Grid, Paper, FormControlLabel, Checkbox, Container, Dialog, IconButton } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Close } from '@mui/icons-material';
import min from '../assets/min.webp';
import min2 from '../assets/min2.png';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { createPropertyEnquiry, getPropertyById } from '../services/apiService';
const sectionTabs = ['Overview', 'Floor Plan', 'Amenities', 'Gallery'];

const PropertyDetails = () => {
  const location = useLocation();
  const { id: urlId } = useParams(); // For backward compatibility
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Overview');
  const [propertyData, setPropertyData] = useState(null);
  // Gallery modal state (move to top)
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIdx, setGalleryIdx] = useState(0);
  // Brochure modal state
  const [brochureModalOpen, setBrochureModalOpen] = useState(false);
  // Enquiry form state
  const [enquiryForm, setEnquiryForm] = useState({
    name: '',
    mobile: '',
    agree: false,
  });
  const [enquirySuccess, setEnquirySuccess] = useState('');
  const [enquiryError, setEnquiryError] = useState('');

  // Brochure form state
  const [brochureForm, setBrochureForm] = useState({
    name: '',
    mobile: '',
    email: '',
    agree: false,
  });
  const [brochureSuccess, setBrochureSuccess] = useState('');
  const [brochureError, setBrochureError] = useState('');

  // Get propertyId from sessionStorage first, then location state, then URL param (for backward compatibility)
  const [propertyId, setPropertyId] = useState(sessionStorage.getItem('propertyId') || location.state?.propertyId || urlId);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let currentPropertyId = propertyId;
        
        // If no propertyId from URL param, try sessionStorage
        if (!currentPropertyId) {
          currentPropertyId = sessionStorage.getItem('propertyId');
          if (currentPropertyId) {
            setPropertyId(currentPropertyId);
          }
        }

        if (!currentPropertyId) {
          navigate('/propertylist'); // Redirect to properties list if no ID
          return;
        }

        const response = await getPropertyById(currentPropertyId);
        if (response && response.status && response.property) {
          setPropertyData(response.property);
          // Clear sessionStorage after successful load
          sessionStorage.removeItem('propertyId');
        } else {
          sessionStorage.removeItem('propertyId');
          navigate('/propertylist'); 
        }
      } catch (error) {
        sessionStorage.removeItem('propertyId'); 
        navigate('/propertylist'); 
      }
    };

    fetchData();
  }, [propertyId, navigate]);

  if (!propertyData) return null;

  // Prepare stats for display
  const propertyStats = [
    {
      iconClass: "fa-solid fa-building-flag",
      label: 'Apartment Type',
      value: propertyData.propertyBhks && propertyData.propertyBhks.length > 0
        ? propertyData.propertyBhks.map(bhk => `${bhk.bhkType} BHK`).join(', ')
        : 'N/A',
    },
    {
      iconClass: 'fa-solid fa-building',
      label: 'Area Range',
      value: `${propertyData.minArea} - ${propertyData.maxArea} sqft`,
    },
    {
      iconClass: "fa-solid fa-building-circle-check",
      label: 'Price Range',
      value: `₹${propertyData.minPrice?.toLocaleString('en-IN')} - ₹${propertyData.maxPrice?.toLocaleString('en-IN')}`,
    },
  ];


  // Gallery fallback - use property image if no gallery images
  const images = propertyData.galleries && propertyData.galleries.length > 0
    ? propertyData.galleries.map(g => g.image)
    : [propertyData.proertyImage];

  // Gallery modal state
  const handleGalleryOpen = (idx) => {
    setGalleryIdx(idx);
    setGalleryOpen(true);
  };
  const handleGalleryClose = () => setGalleryOpen(false);
  const handleGalleryPrev = () => setGalleryIdx((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  const handleGalleryNext = () => setGalleryIdx((prev) => (prev === images.length - 1 ? 0 : prev + 1));

  // Handle enquiry form change
  const handleEnquiryChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEnquiryForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setEnquiryError('');
    setEnquirySuccess('');
  };

  // Handle brochure form change
  const handleBrochureFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBrochureForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setBrochureError('');
    setBrochureSuccess('');
  };

  // Brochure modal handlers
  const handleBrochureModalOpen = () => {
    setBrochureModalOpen(true);
  };

  const handleBrochureModalClose = () => {
    setBrochureModalOpen(false);
    setBrochureForm({ name: '', mobile: '', email: '', agree: false });
    setBrochureError('');
    setBrochureSuccess('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const overviewFields = [
    ['Status', propertyData.status || 'N/A'],
    ['Total Area', `${propertyData.totalArea ? propertyData.totalArea.toLocaleString('en-IN') : 'N/A'} sqft`],
    ['Total Apartments', propertyData.totalApartments || 'N/A'],
    ['Launch Date', formatDate(propertyData.launchDate)],
    ['Availability', propertyData.availability || 'N/A'],
    ['Possession Date', formatDate(propertyData.possessionDate)],
    ['RERA ID', propertyData.reraId || 'N/A'],
    ['Property Type', propertyData.propertyType || 'N/A'],
  ];

  // Handle enquiry form submit
  const handleViewMap = () => {
    if (propertyData.iframeCodeMap) {
      // Extract the URL from the iframe code
      const urlMatch = propertyData.iframeCodeMap.match(/src=["'](.*?)["']/);
      if (urlMatch && urlMatch[1]) {
        // Open the URL in a new tab
        window.open(urlMatch[1], '_blank');
      } else {
        // If no URL found in iframe, try to open a Google Maps search with the location
        const searchQuery = encodeURIComponent(propertyData.iframeCodeMap || '');
        window.open(`https://www.google.com/maps/search/?api=1&query=${searchQuery}`, '_blank');
      }
    } else if (propertyData.location) {
      // Fallback to just searching Google Maps with the location
      const searchQuery = encodeURIComponent(propertyData.location);
      window.open(`https://www.google.com/maps/search/?api=1&query=${searchQuery}`, '_blank');
    }
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setEnquiryError('');
    setEnquirySuccess('');

    // Simple validation
    if (!enquiryForm.name.trim()) {
      setEnquiryError('Please enter your name.');
      return;
    }
    if (!/^\d{10}$/.test(enquiryForm.mobile)) {
      setEnquiryError('Please enter a valid 10-digit mobile number.');
      return;
    }
    if (!enquiryForm.agree) {
      setEnquiryError('You must agree to be contacted.');
      return;
    }

    // Prepare payload (add propertyId)
    const payload = {
      name: enquiryForm.name,
      phoneNumber: enquiryForm.mobile,
      propertyId: propertyData.id,
      allowContact: enquiryForm.agree,
    };

    try {
      await createPropertyEnquiry(payload);
      // setEnquirySuccess('Enquiry submitted successfully!');
      setEnquiryForm({ name: '', mobile: '', "allowContact": true });
    } catch (err) {
      setEnquiryError('Failed to submit enquiry. Please try again.');
    }
  };

  const handleBrochureSubmit = async (e) => {
    e.preventDefault();
    setBrochureError('');
    setBrochureSuccess('');

    // Simple validation
    if (!brochureForm.name.trim()) {
      setBrochureError('Please enter your name.');
      return;
    }
    if (!/^\d{10}$/.test(brochureForm.mobile)) {
      setBrochureError('Please enter a valid 10-digit mobile number.');
      return;
    }
    if (!brochureForm.email.trim() || !/\S+@\S+\.\S+/.test(brochureForm.email)) {
      setBrochureError('Please enter a valid email address.');
      return;
    }
    if (!brochureForm.agree) {
      setBrochureError('You must agree to be contacted.');
      return;
    }

    // Prepare payload for brochure download
    const payload = {
      name: brochureForm.name,
      phoneNumber: brochureForm.mobile,
      email: brochureForm.email,
      propertyId: propertyData.id,
      allowContact: brochureForm.agree,
    };

    try {
      await createPropertyEnquiry(payload);
      setBrochureSuccess('Brochure download initiated! Check your email.');
      
      // Download the brochure after successful API call
      setTimeout(() => {
        handleDownloadBrochure();
        handleBrochureModalClose();
      }, 1000);
    } catch (err) {
      setBrochureError('Failed to submit request. Please try again.');
    }
  };

  const handleDownloadBrochure = () => {
    if (!propertyData.brochure) return;
    // Open PDF in our custom viewer with proper favicon and property name
    const viewerUrl = `/pdf-viewer?url=${encodeURIComponent(propertyData.brochure)}&property=${encodeURIComponent(propertyData.name)}`;
    window.open(viewerUrl, '_blank');
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2, mt: 12 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, }}>
        {/* Left Section */}
        <Box sx={{ flex: { md: 2 } }} >
          {/* Title and Builder */}
          <Typography variant="h2" fontWeight={600} gutterBottom>{propertyData.name} </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <i style={{ color: 'red' }} className="fa-solid fa-location-dot"></i>
            <Typography variant="subtitle1">
              By <span style={{ color: 'red' }}>{propertyData.groupName}</span>
            </Typography>
          </Box>

          {/* Location and Brochure */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
            <Typography variant="body1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              {propertyData.location}
              <Typography
                component="span"
                variant="body1"
                onClick={handleViewMap}
                sx={{
                  color: '#BB081B',
                  ml: 1,
                  cursor: 'pointer',
                  fontFamily: 'Lato, Arial, sans-serif',
                  fontWeight: '500',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                View on Map
              </Typography>
            </Typography>
            {propertyData.brochure && (
              <Button
                disableRipple
                disableFocusRipple
                disableElevation
                variant="contained"
                color="error"
                onClick={handleBrochureModalOpen}
                startIcon={<i className="fas fa-download"></i>}
                sx={{ whiteSpace: 'nowrap', }}
              >
                Download Brochure
              </Button>
            )}
          </Box>

          {/* Project Image */}
          <Box component="img" src={propertyData.proertyImage} alt={propertyData.name} sx={{ mt: 1, width: '100%', height: 350, objectFit: 'cover', borderRadius: 2, }} />

          {/* RERA ID */}
          <Typography variant="body1" mt={2}>  PROJECT RERA ID: {propertyData.reraId} </Typography>

          {/* Stat Cards */}
          <Grid container spacing={2} mt={1}>
            {propertyStats.map((item, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={index}>
                <Paper sx={{ display: 'flex', alignItems: 'center', gap: 2, borderRadius: 2, border: '1px solid #d0d0d0', p: 1, }} elevation={0}>
                  <i className={item.iconClass} style={{ fontSize: 28, color: '#BB081B', minWidth: 32, }}></i>
                  <Box>
                    <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>{item.label}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 16 }}>{item.value}</Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Tab Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 10, background: '#fff', py: 1, borderBottom: '1px solid #eee', }}>
            {sectionTabs.map((label, index) => (
              <Button key={index} variant="contained" onClick={() => setActiveTab(label)} sx={{ textTransform: 'none', width: '170px', backgroundColor: label === activeTab ? '#d60000' : '#f5f5f5', color: label === activeTab ? '#fff' : '#000', boxShadow: 'none', borderRadius: '6px', padding: '6px 16px', fontSize: '14px', }}>  {label} </Button>
            ))}
          </Box>

          {/* Tab Content */}
          {activeTab === 'Overview' && (
            <Box mt={4}>
              <Typography variant="h6" fontWeight={600} mb={3}>  Overview </Typography>
              <Grid container spacing={2}>
                {/* Overview Fields */}
                {overviewFields.map(([label, value], i) => (
                  <Grid size={{ xs: 6, sm: 4 }} key={i}>
                    <Typography variant="subtitle2">{label}</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{value}</Typography>
                  </Grid>
                ))}
              </Grid>
              <Box mt={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>    More about {propertyData.name} </Typography>
                <Typography variant="body2" color="text.secondary"> {propertyData.aboutUs} </Typography>
              </Box>
            </Box>
          )}

          {activeTab === 'Floor Plan' && (
            <Box mt={4}>
              <Typography variant="h6" fontWeight={600} mb={3}>  {propertyData.name} Floor Plans</Typography>
              <Grid container spacing={4}>
                {propertyData.floorplans && propertyData.floorplans.length > 0 ? (
                  propertyData.floorplans.map((plan, idx) => (
                    <Grid size={{ xs: 12, md: 6 }} key={idx}>
                      <Box sx={{ border: '1px solid #ddd', borderRadius: 2, p: 2, display: 'flex', alignItems: 'center', gap: 2, }} >
                        <Box component="img" loading='lazy' src={plan.image} alt="Floor Plan" sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 1, }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flex: 1, }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ color: '#bcbcbc', fontWeight: 500 }}> Area </Typography>
                            <Typography variant="body1" fontWeight={600}>{plan.area} sqft <span style={{ fontSize: '13px' }}>{' '} ({plan.bhkType} )</span></Typography>
                            <Typography variant="body2" color="#bcbcbc" sx={{ mt: 1 }}>    Agreement Price    </Typography>
                            <Typography variant="body1" fontWeight={600}>₹{plan.price}</Typography>
                          </Box>
                          <Box mt={2}>
                            <Button href="tel:+919705401575" variant="outlined" color="error">Enquiry Now</Button>
                          </Box>
                        </Box>
                      </Box>

                    </Grid>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }} >
                    No floor plans available.
                  </Typography>
                )}
              </Grid>
            </Box>
          )}

          {activeTab === 'Amenities' && (
            <Box mt={4}>
              <Typography variant="h6" fontWeight={600} mb={3}>
                {propertyData.name} Amenities
              </Typography>
              <Grid container spacing={4}>
                {propertyData.propertyAmenities.map((item, index) => (
                  <Grid size={{ xs: 6, md: 3 }} key={index}>
                    <Box textAlign="center">
                      <Box sx={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: '#fdecec', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px auto', overflow: 'hidden' }}    >
                        <img loading='lazy' src={item.Amenity?.image} alt={item.Amenity?.name} style={{ width: 40, height: 40, objectFit: 'contain' }} />
                      </Box>
                      <Typography variant="body2">{item.Amenity?.name}</Typography>
                    </Box>
                  </Grid>
                ))}

              </Grid>
            </Box>
          )}

          {activeTab === 'Gallery' && (
            <Box mt={4}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Gallery
              </Typography>
              <Grid container spacing={2} mb={4}>
                {images.map((img, index) => (
                  <Grid size={{ xs: 6, sm: 3 }} key={index}>
                    <Box component="img" loading='lazy' src={img} alt={`Gallery ${index + 1}`}
                      sx={{ width: '150px', height: '150px', borderRadius: 2, objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => handleGalleryOpen(index)}
                    />
                  </Grid>
                ))}
              </Grid>
              {/* Gallery Modal */}
              <Dialog
                open={galleryOpen}
                onClose={handleGalleryClose}
                maxWidth={false}
                PaperProps={{
                  sx: {
                    background: 'transparent',

                    borderRadius: 2,
                    m: 0,
                    p: 0,
                    width: { xs: '90vw', sm: '80vw', md: '70vw' },
                    height: '70vh',
                    maxWidth: '100vw',
                    maxHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: { xs: '90vw', sm: '80vw', md: '70vw' },
                    height: { xs: '60vw', sm: '50vw', md: '40vw' },
                    maxWidth: 1200,
                    maxHeight: 700,
                    minHeight: 300,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0,
                    m: 0,
                  }}
                >
                  <IconButton
                    onClick={handleGalleryClose}
                    sx={{ position: 'absolute', top: 16, right: 16, fontWeight: 600, color: 'black', zIndex: 2 }}
                  >
                    <Close />
                  </IconButton>
                  <IconButton
                    onClick={handleGalleryPrev}
                    sx={{ position: 'absolute', top: '50%', left: { md: 16, xs: 6 }, color: 'black', zIndex: 20, transform: 'translateY(-50%)' }}
                  >
                    <ArrowBackIos />
                  </IconButton>
                  <img
                    src={images[galleryIdx]}
                    alt={`Gallery ${galleryIdx + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 8,
                      boxShadow: '0 4px 32px rgba(0,0,0,0.5)',
                      display: 'block',
                      margin: 'auto'
                    }}
                  />
                  <IconButton
                    onClick={handleGalleryNext}
                    sx={{ position: 'absolute', top: '50%', right: { md: 16, xs: 6 }, color: 'black', zIndex: 2, transform: 'translateY(-50%)' }}
                  >
                    <ArrowForwardIos />
                  </IconButton>
                </Box>
              </Dialog>
            </Box>
          )}
        </Box>

        {/* Right Form Section */}
        <Box sx={{
          flex: { md: 1 }, position: 'sticky', top: "20%", width: { xs: '90%', md: 'auto' }, alignSelf: 'flex-start',
          // height: '100vh',  // overflowY: 'auto'
        }}  >
          <Box sx={{ p: 3, border: '1px solid #ccc', borderRadius: 2, width: { xs: '100%', md: '80%' }, }}  >
            <Typography variant="h6" gutterBottom> Interested to buy property in {propertyData.name}?  </Typography>
            <form onSubmit={handleEnquirySubmit}>
              <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', }} >
                <TextField size="small" label="Name" margin="normal" name="name" value={enquiryForm.name} onChange={handleEnquiryChange} required />
                <TextField size="small" label="Mobile Number" margin="normal" name="mobile" value={enquiryForm.mobile} onChange={handleEnquiryChange} inputProps={{ maxLength: 10, pattern: '[0-9]{10}' }} required
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    name="agree"
                    checked={enquiryForm.agree}
                    onChange={handleEnquiryChange}
                  />
                }
                label={
                  <span style={{ fontSize: '13px', fontWeight: 300 }}>
                    I agree to be contacted by WhatsApp, SMS, Phone & Email
                  </span>
                }
              />

              <Typography variant="body1" sx={{ mt: 1 }}>
                Note : Our Agents will get Back to you within 5 to 10 Minutes.
              </Typography>
              {enquiryError && (<Typography color="error" sx={{ mt: 1 }}>  {enquiryError} </Typography>)}
              {enquirySuccess && (<Typography color="success.main" sx={{ mt: 1 }}> {enquirySuccess} </Typography>)}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button variant="contained" color="error" type="submit"> Get Call Back</Button>
                {/* Info Badges Below Submit Button */}


                
              </Box>
              <Box
  sx={{
    mt: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  }}
>
  {/* Assured Callback */}
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
    <Box
      component="img"
      src={min} // 🔁 Replace with actual path
      alt="Assured Callback"
      sx={{ width: 40, height: 40 }}
    />
    <Box>
      <Typography variant="subtitle1" fontWeight={600}>
        Assured Callback in 5 mins
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Get an assured callback in 5 mins from sales expert (9 AM - 6 PM IST)
      </Typography>
    </Box>
  </Box>

  {/* Authorised Channel Partner */}
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
    <Box
      component="img"
      src={min2} // 🔁 Replace with actual path
      alt="Authorised Channel Partner"
      sx={{ width: 40, height: 40 }}
    />
    <Box>
      <Typography variant="subtitle1" fontWeight={600}>
        Authorised Channel Partner
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Vibrant PropMart is an authorised channel partner for this project
      </Typography>
    </Box>
  </Box>
</Box>
            </form>
          </Box>
        </Box>
      </Box>

      {/* Brochure Download Modal */}
      <Dialog
        open={brochureModalOpen}
        onClose={handleBrochureModalClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 0,
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Modal Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight={600} color="error">
              Download Brochure
            </Typography>
            <IconButton disableFocusRipple disableTouchRipple disableRipple onClick={handleBrochureModalClose}>
              <Close />
            </IconButton>
          </Box>

          {/* Property Information */}
          {/* <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {propertyData.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              By {propertyData.groupName}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {propertyData.location}
            </Typography>
            
            <Grid container spacing={2} mt={2}>
              {propertyStats.map((item, index) => (
                <Grid size={{ xs: 6 }} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <i className={item.iconClass} style={{ fontSize: 16, color: '#BB081B' }}></i>
                    <Box>
                      <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>
                        {item.label}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box> */}

          {/* Form */}
          <form onSubmit={handleBrochureSubmit}>
            <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
              Please provide your details to download the brochure:
            </Typography>
            
            <TextField fullWidth size="small" label="Full Name" margin="normal" name="name"
              value={brochureForm.name}
              onChange={handleBrochureFormChange}
              required
            />
            
            <TextField
              fullWidth
              size="small"
              label="Mobile Number"
              margin="normal"
              name="mobile"
              value={brochureForm.mobile}
              onChange={handleBrochureFormChange}
              inputProps={{ maxLength: 10, pattern: '[0-9]{10}' }}
              required
            />
            
            <TextField
              fullWidth
              size="small"
              label="Email Address"
              margin="normal"
              name="email"
              type="email"
              value={brochureForm.email}
              onChange={handleBrochureFormChange}
              required
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="agree"
                  checked={brochureForm.agree}
                  onChange={handleBrochureFormChange}
                />
              }
              label={
                <span style={{ fontSize: '13px', fontWeight: 300 }}>
                  I agree to be contacted by WhatsApp, SMS, Phone & Email
                </span>
              }
            />

            {brochureError && (
              <Typography color="error" sx={{ mt: 1, fontSize: '14px' }}>
                {brochureError}
              </Typography>
            )}
            
            {brochureSuccess && (
              <Typography color="success.main" sx={{ mt: 1, fontSize: '14px' }}>
                {brochureSuccess}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleBrochureModalClose}
                sx={{ flex: 1 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                type="submit"
                sx={{ flex: 1 }}
                startIcon={<i className="fas fa-download"></i>}
              >
                Download Brochure
              </Button>
            </Box>
          </form>
        </Box>
      </Dialog>
    </Container>

  );
};

export default PropertyDetails;